import React from 'react';
import { SUBSCRIPTION_COLORS } from './subscriptionColors';

export const SubscriptionHistoryModal = ({
  child,
  onClose,
  onSubscribe,
  onExtend,
  onUpgrade,
  t,
  formatDate
}) => {
  const subscriptions = child.subscriptions || [];
  const hasSubscriptions = subscriptions.length > 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="badge bg-success">
            {t('dashboard.index.children.subscription_history.status.active') || 'Active'}
          </span>
        );
      case 'upgraded':
        return (
          <span className="badge bg-info">
            {t('dashboard.index.children.subscription_history.status.upgraded') || 'Upgraded'}
          </span>
        );
      case 'expired':
        return (
          <span className="badge bg-secondary">
            {t('dashboard.index.children.subscription_history.status.expired') || 'Expired'}
          </span>
        );
      default:
        return null;
    }
  };

  const getPackageBadge = (pkg) => {
    if (pkg === 'premium') {
      return (
        <span className="badge" style={SUBSCRIPTION_COLORS.premium.badge}>
          Premium
        </span>
      );
    }
    return (
      <span className="badge" style={SUBSCRIPTION_COLORS.pro.badge}>
        Pro
      </span>
    );
  };

  const getBillingCycleBadge = (cycle) => {
    const label = cycle === 'yearly'
      ? (t('dashboard.index.children.subscription_history.yearly') || 'Yearly')
      : (t('dashboard.index.children.subscription_history.monthly') || 'Monthly');

    return (
      <span className="badge bg-light text-dark border">
        {label}
      </span>
    );
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title d-flex align-items-center gap-2">
              <i className="bi bi-clock-history"></i>
              {t('dashboard.index.children.subscription_history.title') || 'Subscription History'}
              {' - '}{child.username}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {!hasSubscriptions ? (
              <div className="text-center py-5">
                <div className="mb-3">
                  <i className="bi bi-inbox text-muted" style={{ fontSize: '48px' }}></i>
                </div>
                <p className="text-muted mb-0">
                  {t('dashboard.index.children.subscription_history.no_subscriptions') || 'No subscription history yet'}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontSize: '13px' }}>
                        {t('dashboard.index.children.subscription_history.package') || 'Package'}
                      </th>
                      <th style={{ fontSize: '13px' }}>
                        {t('dashboard.index.children.subscription_history.billing_cycle') || 'Billing'}
                      </th>
                      <th style={{ fontSize: '13px' }}>
                        {t('dashboard.index.children.subscription_history.started_at') || 'Started'}
                      </th>
                      <th style={{ fontSize: '13px' }}>
                        {t('dashboard.index.children.subscription_history.ended_at') || 'Ends'}
                      </th>
                      <th style={{ fontSize: '13px' }}>
                        {t('dashboard.index.children.subscription_history.status_label') || 'Status'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => {
                      // Show upgrade button for active Pro subscriptions when child can upgrade
                      const canUpgradeThisSub = child.can_upgrade_to_premium && sub.package === 'pro' && sub.status === 'active';

                      return (
                        <tr key={sub.id}>
                          <td className="align-middle">
                            <div className="d-flex align-items-center gap-2">
                              {getPackageBadge(sub.package)}
                              {canUpgradeThisSub && (
                                <button
                                  type="button"
                                  className="btn btn-sm d-flex align-items-center justify-content-center p-0"
                                  style={{
                                    ...SUBSCRIPTION_COLORS.premium.button,
                                    border: 'none',
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px'
                                  }}
                                  onClick={onUpgrade}
                                  title={t('dashboard.index.children.subscription.upgrade_premium') || 'Upgrade to Premium'}
                                  data-testid="upgrade-button"
                                >
                                  <i className="bi bi-arrow-up" style={{ fontSize: '12px' }}></i>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="align-middle">
                            {getBillingCycleBadge(sub.billing_cycle)}
                          </td>
                          <td className="align-middle" style={{ fontSize: '13px' }}>
                            {formatDate(sub.started_at)}
                          </td>
                          <td className="align-middle" style={{ fontSize: '13px' }}>
                            {formatDate(sub.ended_at)}
                          </td>
                          <td className="align-middle">
                            {getStatusBadge(sub.status)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            {/* Extend/Subscribe button */}
            <button
              type="button"
              className="btn btn-success d-flex align-items-center gap-2"
              onClick={hasSubscriptions ? onExtend : onSubscribe}
            >
              <i className="bi bi-plus-lg"></i>
              {hasSubscriptions
                ? (t('dashboard.index.children.subscription_history.extend') || 'Extend Subscription')
                : (t('dashboard.index.children.subscription_history.subscribe') || 'Subscribe Now')
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
